import { gql, useMutation } from '@apollo/client';
import { PATCH_LAST_RECURRING_PAYMENT } from '@mutations/patchLastRecurringPayment';
import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { BottomButtonsLayout } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { isBefore } from 'date-fns';
import { useAttachmentChanges } from 'hooks/useAttachmentChanges';
import useIsRecurringPaymentRetryWindowExpired from 'hooks/useIsRecurringPaymentRetryWindowExpired';
import usePollSalesOrderLastRecurringPaymentStatus from 'hooks/usePollSalesOrderLastRecurringPaymentStatus';
import { useSalesOrderAttachmentScreen } from 'hooks/useSalesOrderAttachmentScreen';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useUpdateSalesOrder from 'hooks/useUpdateSalesOrder';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  clearPermitEditLicensePlateServerSeed,
  parkingPaymentCardId,
  syncPermitEditLicensePlatesFromServer,
} from 'states/common';
import { useAttachmentStore } from 'stores/attachmentStore';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import {
  LicensePlate,
  Mutation,
  MutationPatchLastRecurringPaymentArgs,
  SalesOrderActionType,
  UpdateSalesOrderInput,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import {
  isRequiredSalesOrderAttachmentMissing,
  resolveAttachmentUi,
  revokeAttachmentUri,
} from 'utils/attachmentUtils';
import { updateCache } from 'utils/cacheUtils';
import { isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';
import { isCardSelectable } from 'utils/paymentUtils';
import {
  getSalesOrderRegistrationPlatesForEdit,
  isTerminalOrderStatus,
  registrationPlatesContentEqual,
  showLicensePlate,
} from 'utils/permitUtils';
import ModalContentWrapper from '../ModalContentWrapper';
import { SalesOrderReceipt } from '../SalesOrderReceiptClass';
import SalesOrderPermitEditFooter from './SalesOrderPermitEditFooter';
import SalesOrderTicket from './SalesOrderTicket';

// The retry result is the main feedback on this screen; on mobile web the
// default toast placement is the bottom, next to the CTA.
const TOP_TOAST = { placement: 'top' };

const SalesOrderPermitEdit: React.FC = () => {
  const navigation = useNavigation<RootNavProps>();
  const { navigate, goBack } = navigation;
  const navigationState = navigation.getState();
  const previousRoute = navigationState.routes[navigationState.index - 1];
  const {
    params: { orderLineIdWithVersion },
  } = useRoute<RouteProp<RootStackParamList, 'salesOrderPermitEdit'>>();
  const {
    refetchCustomer,
    refetchPermits,
    customer: { permits },
  } = useContext(AccountContext);

  const salesOrder = permits?.find(
    (sOrder) => sOrder?.orderLineIdWithVersion === orderLineIdWithVersion
  );
  const salesOrderId = salesOrder?.id;
  const validFrom = salesOrder?.validity?.validFrom ?? new Date();
  const validFromInPast = isBefore(validFrom, new Date());

  const salesOrderReceipt = new SalesOrderReceipt(salesOrder);
  const { isFailedRecurringPaymentForSalesOrder } = salesOrderReceipt;
  const isRetryWindowExpired =
    useIsRecurringPaymentRetryWindowExpired(salesOrderReceipt);

  const {
    orderLines,
    renewal,
    creditCardId,
    isInvoicing,
    isWaiting: isWaitingForActivation,
    isWaitingActivationOrCSR,
    orderStatus,
  } = salesOrderReceipt || {};

  const isTerminalStatus = isTerminalOrderStatus(orderStatus);

  const orderLine = orderLines?.[0];
  const { pmcId, orderLineId, lineTotal, quantity, version } = orderLine ?? {};

  const originalRegistrationPlates: LicensePlate[] = useMemo(
    () => getSalesOrderRegistrationPlatesForEdit(salesOrder, orderLine),
    [salesOrder, orderLine]
  );

  const skipNextPermitEditPlateSyncRef = useRef(false);

  const [isRecurring, setIsRecurring] = useState<boolean | null | undefined>(
    renewal
  );
  const { t } = useTranslation();
  const paymentMethod = useSelectedPaymentMethod();
  const selectedLicensePlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );
  const confirmDisableRenewalAction = usePermitStore(
    (state) => state.confirmDisableRenewalAction
  );
  const setConfirmDisableRenewalAction = usePermitStore(
    (state) => state.setConfirmDisableRenewalAction
  );

  const handleToggleRecurring = useCallback(
    (value: boolean | null | undefined) => {
      if (Boolean(renewal) && value === false) {
        usePermitStore.getState().setSelectedPermit(orderLineIdWithVersion);
        usePermitStore.getState().setConfirmDisableRenewalOnly(true);
        usePermitStore.getState().setShowDisableRenewalDialog(true);
        useUiStore.getState().setSheetModal('terminatePermit');
        return;
      }
      setIsRecurring(value);
    },
    [renewal, orderLineIdWithVersion]
  );

  useEffect(() => {
    if (confirmDisableRenewalAction !== 'confirm') {
      return;
    }
    setConfirmDisableRenewalAction(null);
    setIsRecurring(false);
  }, [confirmDisableRenewalAction, setConfirmDisableRenewalAction]);

  const isFreeSalesOrder = lineTotal === 0;

  const handleNavigateAfterUpdate = () => {
    if (previousRoute?.name === 'mainTabs') {
      navigate('salesOrderPermitDetails', {
        orderLineIdWithVersion: salesOrder?.orderLineIdWithVersion ?? '',
      });
    } else {
      goBack();
    }
  };

  const orderUid = salesOrderId ?? null;
  const orderAttachments = salesOrder?.attachments;
  const { showOnSalesOrder, requireOnSalesOrder } = resolveAttachmentUi(
    orderLine?.pmc?.attachmentPolicy
  );
  const showAttachment =
    !!orderUid && (showOnSalesOrder || !!orderAttachments?.length);

  const { markChanged, attachmentUidsToSubmit } = useAttachmentChanges(
    orderUid,
    orderAttachments
  );

  useSalesOrderAttachmentScreen({
    orderUid,
    orderAttachments,
    enableSalesOrderAttachments: showOnSalesOrder,
    additionalReadOnly: isTerminalStatus,
    onAttachmentChanged: markChanged,
  });

  const liveAttachments = useAttachmentStore((state) => state.attachments);
  const uploadedAttachmentCount = useMemo(() => {
    if (attachmentUidsToSubmit !== undefined) {
      return attachmentUidsToSubmit.length;
    }
    if (orderAttachments?.length) {
      return orderAttachments.filter(({ uid }) => !!uid).length;
    }
    if (!orderUid) {
      return 0;
    }
    return liveAttachments.filter(
      (a) => a.contextId === orderUid && !!a.uploadUid
    ).length;
  }, [attachmentUidsToSubmit, orderAttachments, liveAttachments, orderUid]);

  const blockSaveWithoutRequiredAttachment =
    isWaitingActivationOrCSR &&
    isRequiredSalesOrderAttachmentMissing(
      requireOnSalesOrder,
      uploadedAttachmentCount
    );

  const hasAttachmentChanges = attachmentUidsToSubmit !== undefined;

  const { updateSalesOrder, loading: updateLoading } = useUpdateSalesOrder({
    customUpdate: (cache, { data }) => {
      if (salesOrderId) {
        updateCache(
          cache,
          {
            __typename: 'UnifiedPermit',
            id: salesOrderId,
            orderLineIdWithVersion,
          },
          {
            fragment: gql`
              fragment UnifiedPermitFragment2 on UnifiedPermit {
                creditCardId
                renewal
                orderStatus
                paymentState
              }
            `,
            data: {
              creditCardId: data?.updateSalesOrder?.creditCardId,
              renewal: data?.updateSalesOrder?.renewal,
              orderStatus: isWaitingForActivation
                ? 'ACTIVATING'
                : data?.updateSalesOrder?.orderStatus,
              paymentState: data?.updateSalesOrder?.paymentState,
            },
          }
        );
      }
    },
  });

  const [skipPolling, setSkipPolling] = useState(true);
  // Stays true until the screen is left, so the processing state does not
  // drop between the mutation, the polling and the navigation.
  const [isRetryingPayment, setIsRetryingPayment] = useState(false);
  const [isRetrySucceeded, setIsRetrySucceeded] = useState(false);

  const { lastPollingCompleted, isFailed } =
    usePollSalesOrderLastRecurringPaymentStatus(
      {
        salesOrderUid: salesOrderId ?? '',
      },
      skipPolling
    );

  // Stay on the screen so the user can retry again.
  const handleRetryFailed = () => {
    setIsRetryingPayment(false);
    showToast(
      t('permit.permitRecurringPaymentFailed'),
      'error',
      undefined,
      TOP_TOAST
    );
  };

  const refetchAfterRetry = async () => {
    try {
      // The permit list holds the payment state shown on the previous screens.
      await Promise.all([refetchPermits(), refetchCustomer()]);
    } catch (error) {
      Logger.error(
        `refetch after payment retry failed | orderId=${salesOrderId} | ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  useEffect(() => {
    if (!lastPollingCompleted || skipPolling) return;

    setSkipPolling(true);

    if (isFailed === false) {
      // Stay on the screen. The receipt still says failed until the refetch
      // lands, and the footer must not fall back to the retry state meanwhile.
      setIsRetryingPayment(false);
      setIsRetrySucceeded(true);
      // The retry turns the renewal on, so the refreshed permit must not read
      // as an unsaved change.
      setIsRecurring(true);
      showToast(
        t('permit.permitRecurringPaymentSuccess'),
        'success',
        undefined,
        TOP_TOAST
      );
      void refetchAfterRetry();
      return;
    }

    handleRetryFailed();
  }, [lastPollingCompleted]);

  const [patchLastRecurringPayment] = useMutation<
    Mutation,
    MutationPatchLastRecurringPaymentArgs
  >(PATCH_LAST_RECURRING_PAYMENT, {
    onCompleted: () => {
      setSkipPolling(false);
    },
    onError: handleRetryFailed,
  });

  useEffect(() => {
    setBlockCompanybenefitInEdit('permitEdit');
    return () => {
      setBlockCompanybenefitInEdit('');
      parkingPaymentCardId(undefined);
    };
  }, []);

  const syncedCardSeedRef = useRef('');

  const applyServerPlatesToEdit = useCallback(() => {
    syncPermitEditLicensePlatesFromServer(
      orderLineIdWithVersion,
      originalRegistrationPlates,
      creditCardId,
      originalRegistrationPlates
    );
  }, [creditCardId, orderLineIdWithVersion, originalRegistrationPlates]);

  useLayoutEffect(() => {
    const cardSeed = `${orderLineIdWithVersion ?? ''}@@${creditCardId ?? ''}`;
    if (syncedCardSeedRef.current !== cardSeed) {
      syncedCardSeedRef.current = cardSeed;
      parkingPaymentCardId(creditCardId || undefined);
    }

    if (!skipNextPermitEditPlateSyncRef.current) {
      applyServerPlatesToEdit();
    }
  }, [applyServerPlatesToEdit, creditCardId, orderLineIdWithVersion]);

  useFocusEffect(
    useCallback(() => {
      if (skipNextPermitEditPlateSyncRef.current) {
        skipNextPermitEditPlateSyncRef.current = false;
        return;
      }
      applyServerPlatesToEdit();
    }, [applyServerPlatesToEdit])
  );

  const handleVehiclesSelect = useCallback(() => {
    skipNextPermitEditPlateSyncRef.current = true;
    navigate('permitVehicles', {
      permitId: orderLineIdWithVersion ?? '',
    });
  }, [navigate, orderLineIdWithVersion]);

  const orderLineIdWithVersionRef = useRef(orderLineIdWithVersion);
  orderLineIdWithVersionRef.current = orderLineIdWithVersion;
  useEffect(() => {
    return () => {
      if (skipNextPermitEditPlateSyncRef.current) {
        return;
      }
      clearPermitEditLicensePlateServerSeed(orderLineIdWithVersionRef.current);
      usePermitStore.getState().setSelectedLicensePlatesPermitRedeem([]);
    };
  }, []);

  // Discard pending uploads on unmount if changes were never saved.
  const unsavedAttachmentsRef = useRef(false);
  unsavedAttachmentsRef.current = hasAttachmentChanges;
  const orderUidRef = useRef(orderUid);
  orderUidRef.current = orderUid;
  useEffect(() => {
    return () => {
      if (!unsavedAttachmentsRef.current) return;
      const uid = orderUidRef.current;
      if (!uid) return;
      const { attachments: current, setAttachments } =
        useAttachmentStore.getState();
      let mutated = false;
      const next = current.filter((a) => {
        if (a.contextId !== uid) return true;
        if (!a.uri) return true;
        revokeAttachmentUri(a.uri);
        mutated = true;
        return false;
      });
      if (mutated) setAttachments(next);
    };
  }, []);

  const { cardId, status } = paymentMethod || {};

  const platesUnchanged = registrationPlatesContentEqual(
    selectedLicensePlates,
    originalRegistrationPlates
  );
  const renewalUnchanged = Boolean(renewal) === Boolean(isRecurring);
  const paymentUnchanged =
    isInvoicing ||
    isFreeSalesOrder ||
    (creditCardId ?? undefined) === (cardId ?? undefined);
  const hasNoEdits = platesUnchanged && renewalUnchanged && paymentUnchanged;

  const canRetryRecurringPayment =
    !!cardId && isCardSelectable(status) && !isRetryWindowExpired;

  const hasUnsavedChanges = hasAttachmentChanges || !hasNoEdits;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const allowLeaveRef = useRef(false);
  const pendingAction = usePermitStore(
    (state) => state.unsavedEditPermitAction
  );
  const setUnsavedEditPermitAction = usePermitStore(
    (state) => state.setUnsavedEditPermitAction
  );
  const setBlockCompanybenefitInEdit = usePermitStore(
    (state) => state.setBlockCompanybenefitInEdit
  );

  const submitRecurringPaymentRetry = () => {
    if (!salesOrderId) {
      showToast(t('error.updatePermit'), 'error');
      return;
    }
    setIsRetryingPayment(true);
    patchLastRecurringPayment({
      variables: { input: { salesOrderUid: salesOrderId, cardId } },
    });
  };

  const buildUpdateInput = (): UpdateSalesOrderInput => {
    const waitingExtras = isWaitingForActivation
      ? {
          validFrom: validFromInPast ? new Date() : validFrom,
          orderStatus: 'ACTIVATING',
        }
      : {};
    const attachmentExtras = attachmentUidsToSubmit
      ? { attachmentUids: attachmentUidsToSubmit }
      : {};
    return {
      salesOrderId,
      creditCardId: !isInvoicing && !isFreeSalesOrder ? cardId : undefined,
      renewal: isRecurring,
      registrationPlates: selectedLicensePlates,
      quantity,
      version,
      pmcId,
      orderLineId,
      hasChanges: isWaitingForActivation,
      ...waitingExtras,
      ...attachmentExtras,
      updateType: 'PATCH' as SalesOrderActionType,
    } as UpdateSalesOrderInput;
  };

  const trySubmitUpdate = async (): Promise<boolean> => {
    try {
      const result = await updateSalesOrder({
        variables: { input: buildUpdateInput() },
      });
      if (result.errors?.length) {
        showToast(t('error.updatePermit'), 'error');
        return false;
      }
      return true;
    } catch (e) {
      Logger.error(
        `updateSalesOrder in permit edit failed | orderId=${salesOrderId} | ${e instanceof Error ? e.message : String(e)}`
      );
      showToast(t('error.updatePermit'), 'error');
      return false;
    }
  };

  const safeRefetchPermits = async () => {
    try {
      await refetchPermits();
    } catch (e) {
      Logger.error(
        `refetchPermits after permit edit failed | orderId=${salesOrderId} | ${e instanceof Error ? e.message : String(e)}`
      );
    }
  };

  const confirmEditPermit = async () => {
    if (isFailedRecurringPaymentForSalesOrder) {
      submitRecurringPaymentRetry();
      return;
    }
    if (blockSaveWithoutRequiredAttachment) {
      if (!isWeb) {
        useUiStore.getState().setSheetModal('addAttachment');
      }
      return;
    }
    const ok = await trySubmitUpdate();
    if (!ok) {
      handleNavigateAfterUpdate();
      return;
    }
    showToast(t('success.updatePermit'), 'success');
    if (hasAttachmentChanges) await safeRefetchPermits();
    handleNavigateAfterUpdate();
  };

  // Browser back is a route POP; close the disable-renewal sheet first.
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (useUiStore.getState().sheetModal === 'terminatePermit') {
        e.preventDefault();
        useUiStore.getState().setSheetModal('');
        return;
      }
      if (allowLeaveRef.current) return;
      if (!hasUnsavedChangesRef.current) return;
      e.preventDefault();
      useUiStore.getState().setSheetModal('unsavedEditPermit');
    });
    return unsubscribe;
  }, [navigation]);

  const handleNavigateAfterUpdateRef = useRef(handleNavigateAfterUpdate);
  handleNavigateAfterUpdateRef.current = handleNavigateAfterUpdate;
  const confirmEditPermitRef = useRef(confirmEditPermit);
  confirmEditPermitRef.current = confirmEditPermit;

  useEffect(() => {
    if (!pendingAction) return;
    setUnsavedEditPermitAction(null);
    allowLeaveRef.current = true;
    if (pendingAction === 'discard') {
      hasUnsavedChangesRef.current = false;
      handleNavigateAfterUpdateRef.current();
      return;
    }
    confirmEditPermitRef.current().catch(() => undefined);
  }, [pendingAction]);

  const requiresLicensePlate = showLicensePlate(salesOrder);

  return (
    <BottomButtonsLayout
      contentBottom={
        <SalesOrderPermitEditFooter
          confirmEditPermit={confirmEditPermit}
          onVehiclesSelect={handleVehiclesSelect}
          disabled={
            isFailedRecurringPaymentForSalesOrder
              ? !canRetryRecurringPayment
              : (requiresLicensePlate && !selectedLicensePlates.length) ||
                (!isWaitingActivationOrCSR &&
                  hasNoEdits &&
                  !hasAttachmentChanges) ||
                (!isInvoicing && !isFreeSalesOrder && !cardId) ||
                (isWeb && blockSaveWithoutRequiredAttachment)
          }
          missingRequiredAttachment={blockSaveWithoutRequiredAttachment}
          loading={isRetryingPayment || updateLoading}
          hasCard={!!cardId}
          hasLicensePlate={originalRegistrationPlates.length > 0}
          freeSalesOrder={isFreeSalesOrder}
          showLicensePlate={requiresLicensePlate}
          salesOrderReceipt={salesOrderReceipt}
          paymentRetrySucceeded={
            isRetrySucceeded && isFailedRecurringPaymentForSalesOrder
          }
        />
      }>
      <ModalContentWrapper>
        <SalesOrderTicket
          salesOrder={salesOrder}
          allowRecurringToggle
          isRecurring={isRecurring}
          toggleRecurringCallback={handleToggleRecurring}
          showAttachment={showAttachment}
        />
      </ModalContentWrapper>
    </BottomButtonsLayout>
  );
};

export default SalesOrderPermitEdit;
